import React, { useEffect, useRef } from 'react';
import { Chart, ArcElement, BarElement, LineElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController, LineController, PointElement } from 'chart.js';

// Register all necessary components
Chart.register(ArcElement, BarElement, LineElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController, LineController, PointElement);

export default function DashboardHome() {
  const paymentRef = useRef(null);
  const typeRef = useRef(null);
  const subsRef = useRef(null);
  const netRef = useRef(null);

  // Store chart instances to destroy them later
  const paymentChartInstance = useRef(null);
  const typeChartInstance = useRef(null);
  const subsChartInstance = useRef(null);
  const netChartInstance = useRef(null);

  useEffect(() => {
    // Destroy existing chart instances to prevent "Canvas is already in use" error
    if (paymentChartInstance.current) paymentChartInstance.current.destroy();
    if (typeChartInstance.current) typeChartInstance.current.destroy();
    if (subsChartInstance.current) subsChartInstance.current.destroy();
    if (netChartInstance.current) netChartInstance.current.destroy();

    // Donations As Per Payment Method (Doughnut)
    paymentChartInstance.current = new Chart(paymentRef.current, {
      type: 'doughnut',
      data: {
        labels: ['AID Bank Account', 'AID Pay Bank', 'AID Pay CC', 'Cash', 'Terminal', 'Website CC'],
        datasets: [{
          data: [30, 20, 15, 10, 8, 17],
          backgroundColor: ['#cbb69d', '#e1c699', '#f4b083', '#f9e2c0', '#fee2b3', '#e0d4c9'],
        }],
      },
      options: { plugins: { legend: { position: 'right' } } },
    });

    // Top Donations Type (Doughnut)
    typeChartInstance.current = new Chart(typeRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Programs', 'Events', 'Services'],
        datasets: [{
          data: [40, 30, 30],
          backgroundColor: ['#2563eb', '#60a5fa', '#bfdbfe'],
        }],
      },
      options: { plugins: { legend: { position: 'right' } } },
    });

    // Subscribers Per Day (Bar)
    subsChartInstance.current = new Chart(subsRef.current, {
      type: 'bar',
      data: {
        labels: Array.from({ length: 31 }, (_, i) => i + 1),
        datasets: [{
          data: Array.from({ length: 31 }, () => Math.floor(5 + Math.random() * 20)),
          backgroundColor: '#22c55e',
        }],
      },
      options: {
        scales: {
          x: { title: { display: true, text: 'Date' } },
          y: { title: { display: true, text: 'Subscribers' }, min: 5, max: 60 },
        },
        plugins: { legend: { display: false } },
      },
    });

    // Last 30 Days Net Donation (Line)
    netChartInstance.current = new Chart(netRef.current, {
      type: 'line',
      data: {
        labels: Array.from({ length: 31 }, (_, i) => i + 1),
        datasets: [{
          data: Array.from({ length: 31 }, () => Math.floor(10 + Math.random() * 80)),
          fill: 0,
          tension: 0.4,
          borderColor: '#0066db',
          pointRadius: 3,
          pointBackgroundColor: '#0066db',
        }],
      },
      options: {
        scales: {
          x: { title: { display: true, text: 'Date' } },
          y: { title: { display: true, text: 'Donation Amount in USD' } },
        },
        plugins: { legend: { display: false } },
      },
    });

    // Cleanup on component unmount
    return () => {
      if (paymentChartInstance.current) paymentChartInstance.current.destroy();
      if (typeChartInstance.current) typeChartInstance.current.destroy();
      if (subsChartInstance.current) subsChartInstance.current.destroy();
      if (netChartInstance.current) netChartInstance.current.destroy();
    };
  }, []);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Dashboard</h1>
      <div style={styles.row}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Donations As Per Payment Method</h3>
          <div style={styles.chartDoughnut2x2}><canvas ref={paymentRef} /></div>
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Top Donations Type</h3>
          <div style={styles.chartDoughnut2x2}><canvas ref={typeRef} /></div>
        </div>
      </div>
      <div style={styles.cardFull}>
        <div style={styles.cardHeader}>
          <h3>Subscribers Per Day</h3>
          <select style={styles.select}>
            <option>Last 30 Days</option>
          </select>
        </div>
        <canvas ref={subsRef} />
      </div>
      <div style={styles.cardFull}>
        <div style={styles.cardHeader}>
          <h3>Last 30 Days Net Donation</h3>
          <select style={styles.select}>
            <option>Last 30 Days</option>
          </select>
        </div>
        <canvas ref={netRef} />
      </div>
    </div>
  );
}

const styles = {
  container: { margin: '0 auto', color: '#333' },
  title: { fontSize: 24, marginBottom: 16, marginTop: 0, color: '#4a5568' },
  row: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  card: { background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 16, flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column' },
  cardTitle: { margin: 0, marginBottom: 8, fontSize: 16 },
  cardFull: { background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  select: { padding: '4px 8px', fontSize: 14 },
  chartDoughnut2x2: { 
    height: 450,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center' },
}